import { ChevronDown, ChevronUp, X } from 'lucide-react'

import { useDataTable } from '@/components/data-table/data-table-provider'
import { useSheetRowNavigation } from '@/hooks/useSheetRowNavigation'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { MetaCard } from '@/components/SheetSectionCard'
import { formatSheetTimestamp } from '@/lib/format'
import type { ProviderRecord } from '@/features/providers/types'

/**
 * Providers details sheet — renders the selected row (from the DataTable
 * context via the `sheetSlot` prop) into titled section cards. All provider
 * fields are already on the row, so no detail fetch is needed.
 */
export function ProvidersSheetWrapper() {
  const { table, rowSelection, isLoading } = useDataTable<
    ProviderRecord,
    unknown
  >()

  const {
    selectedRowKey,
    selectedRow,
    prevId,
    nextId,
    onPrev,
    onNext,
    onOpenChange,
  } = useSheetRowNavigation(table, rowSelection, !!isLoading)

  const provider = selectedRow?.original
  const subtitle = provider
    ? [provider.credentials, provider.specialty].filter(Boolean).join(' · ')
    : ''

  return (
    <Sheet open={!!selectedRowKey} onOpenChange={onOpenChange}>
      <SheetContent
        onCloseAutoFocus={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="bg-neutral-ui-bg w-full max-w-none gap-0 overflow-y-auto rounded-l-xl p-0 sm:max-w-lg"
        showCloseButton={false}>
        <SheetHeader className="bg-neutral-ui-bg sticky top-0 z-10">
          <div className="flex items-start justify-between gap-2">
            <SheetTitle className="min-w-0 flex-1 truncate text-left text-lg font-semibold">
              {provider?.provider_name || 'Provider Details'}
            </SheetTitle>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={!prevId}
              onClick={onPrev}
              aria-label="Previous provider"
              title="Previous provider (↑)">
              <ChevronUp className="h-5 w-5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={!nextId}
              onClick={onNext}
              aria-label="Next provider"
              title="Next provider (↓)">
              <ChevronDown className="h-5 w-5" />
            </Button>
            <SheetClose asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </SheetClose>
          </div>
          {subtitle ? (
            <div className="text-neutral-low-contrast text-sm">{subtitle}</div>
          ) : null}
        </SheetHeader>

        <SheetDescription className="sr-only">
          Selected provider details
        </SheetDescription>

        <div className="px-4 pt-0 pb-4">
          {provider ? (
            <div className="space-y-4">
              <MetaCard
                title="Provider"
                rows={[
                  { label: 'Name', value: provider.provider_name },
                  { label: 'Credentials', value: provider.credentials },
                  { label: 'Specialty', value: provider.specialty },
                ]}
              />
              <MetaCard
                title="Record"
                rows={[
                  {
                    label: 'Provider ID',
                    value: (
                      <span className="font-mono text-xs break-all select-all">
                        {provider.id}
                      </span>
                    ),
                  },
                  {
                    label: 'Created',
                    value: provider.created_at
                      ? formatSheetTimestamp(provider.created_at)
                      : null,
                  },
                ]}
              />
            </div>
          ) : isLoading ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              Loading…
            </p>
          ) : (
            <p className="text-muted-foreground py-8 text-center text-sm">
              Select a row to view details
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
